import React from 'react'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { COURSE_TYPES, VIDEO_COURSE_TYPES } from '../../../../../utils/constants'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { Button, DialogContent, FormHelperText, useTheme } from '@mui/material'
import SingleSelect from '../../../../../components/muiComponents/singleSelect'
import { Box } from '@mui/system'
import FlagKz from '../../../../../assets/img/flag-kz.png'
import FlagRu from '../../../../../assets/img/flag-ru.png'
import { CourseState } from '../../../../../features/types'
import { H3 } from '../../../../../layouts/TextStyles'
import CustomInput from '../../../../../components/muiComponents/customInput'
import { CustomRichTextEditor } from '../../../../../components/customRichTextEditor'

interface Props {
    onImageKzChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onImageRuChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    formik: any
    imageKz: string
    imageRu: string
    editCourse?: CourseState
}

const AddCourseContent: React.FC<Props> = ({
    onImageKzChange,
    onImageRuChange,
    formik,
    imageKz,
    imageRu,
    editCourse,
}) => {
    const theme = useTheme()
    const { formatMessage } = useIntl()
    const styles = {
        boxContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
        },
        textEditor: {
            minWidth: '400px',
            width: '100%',
            minHeight: '300px',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            mt: 1,
            mb: 1,
        },
        error: {
            color: '#d32f2f',
            marginLeft: '12px',
        },
        img: {
            color: '#5185FF',
            width: '48px',
            height: '48px',
        },
        flag: {
            width: 24,
            height: 24,
            marginLeft: 10,
            marginTop: 6,
        },
        titleBox: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
    }

    const uploadImageKz = () => (
        <Box>
            <Button
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<FileUploadIcon />}
            >
                Сурет жүктеу
                <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={onImageKzChange}
                />
            </Button>

            <FormHelperText style={styles.error}>
                {formik.touched.imageKz && formik.errors.imageKz}
            </FormHelperText>

            {formik.values.imageKz && (
                <ImageDiv>
                    <img
                        src={imageKz}
                        alt="flag"
                        style={styles.img}
                    />

                    <span>
                        {editCourse?.imageKz ?? formik.values.imageKz['name']}
                    </span>
                </ImageDiv>
            )}
        </Box>
    )

    const uploadImageRu = () => (
        <Box>
            <Button
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<FileUploadIcon />}
            >
                Загрузить фото
                <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={onImageRuChange}
                />
            </Button>

            <FormHelperText style={styles.error}>
                {formik.touched.imageRu && formik.errors.imageRu}
            </FormHelperText>

            {formik.values.imageRu && (
                <ImageDiv>
                    <img
                        src={imageRu}
                        alt="flag"
                        style={styles.img}
                    />

                    <span>
                        {editCourse?.imageRu ?? formik.values.imageRu['name']}
                    </span>
                </ImageDiv>
            )}
        </Box>
    )

    return (
        <DialogContent>
            <form>
                <Box sx={styles.boxContainer}>
                    <Box
                        display={'flex'}
                        flexDirection="column"
                        minWidth={500}
                    >
                        <Box sx={{ height: '160px' }}>
                            <Box sx={styles.titleBox}>
                                <H3>Курс аты</H3>

                                <img
                                    alt="flag"
                                    src={FlagKz}
                                    style={styles.flag}
                                />
                            </Box>

                            <CustomInput
                                id="titleKz"
                                name="titleKz"
                                value={formik.values.titleKz}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.titleKz)}
                                helperText={formik.errors.titleKz}
                                placeholder="Enter name"
                                fullWidth
                            />
                        </Box>

                        <Box mb={2}>
                            <H3>Курс сипаттамасы</H3>

                            <Box
                                className="CustomEditor"
                                sx={styles.textEditor}
                            >
                                <CustomRichTextEditor
                                    value={formik.values.descriptionKz}
                                    onEditorChange={(text) => {
                                        formik.handleChange({
                                            target: {
                                                name: `descriptionKz`,
                                                value: text,
                                            },
                                        })
                                    }}
                                    inline
                                    editable
                                />
                            </Box>

                            <FormHelperText sx={styles.error}>
                                {formik.errors.descriptionKz}
                            </FormHelperText>
                        </Box>

                        {uploadImageKz()}

                        <Box sx={{ height: '160px', mt: 4 }}>
                            <Box sx={styles.titleBox}>
                                <H3>{formatMessage({id: "courseType"})}</H3>
                            </Box>

                            <SingleSelect
                                id="courseType"
                                name="courseType"
                                placeholder={formatMessage({
                                    id: 'courseType',
                                })}
                                value={formik.values.courseType}
                                menuItems={COURSE_TYPES}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                handleError={Boolean(
                                    formik.errors.courseType,
                                )}
                            />

                            <FormHelperText style={styles.error}>
                                {formik?.touched?.courseType && formik?.errors?.courseType}
                            </FormHelperText>
                        </Box>
                    </Box>

                    <Box
                        display={'flex'}
                        flexDirection="column"
                        minWidth={500}
                    >
                        <Box sx={{ height: '160px' }}>
                            <Box sx={styles.titleBox}>
                                <H3>Название курса</H3>

                                <img
                                    alt="flag"
                                    src={FlagRu}
                                    style={styles.flag}
                                />
                            </Box>

                            <CustomInput
                                id="titleRu"
                                name="titleRu"
                                value={formik.values.titleRu}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.titleRu)}
                                helperText={formik.errors.titleRu}
                                placeholder="Enter name"
                                fullWidth
                            />
                        </Box>

                        <Box mb={2}>
                            <H3>Описание курса</H3>

                            <Box
                                className="CustomEditor"
                                sx={styles.textEditor}
                            >
                                <CustomRichTextEditor
                                    value={formik.values.descriptionRu}
                                    onEditorChange={(text) => {
                                        formik.handleChange({
                                            target: {
                                                name: `descriptionRu`,
                                                value: text,
                                            },
                                        })
                                    }}
                                    inline
                                    editable
                                />
                            </Box>

                            <FormHelperText sx={styles.error}>
                                {formik.errors.descriptionRu}
                            </FormHelperText>
                        </Box>

                        {uploadImageRu()}

                        <Box sx={{ height: '160px', mt: 4 }}>
                            <Box sx={styles.titleBox}>
                                <H3>{formatMessage({id: "videoCourseType"})}</H3>
                            </Box>

                            <SingleSelect
                                id="videoCourseType"
                                name="videoCourseType"
                                placeholder={formatMessage({
                                    id: 'videoCourseType',
                                })}
                                value={formik.values.videoCourseType}
                                menuItems={VIDEO_COURSE_TYPES}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                handleError={Boolean(
                                    formik?.errors?.videoCourseType,
                                )}
                            />

                            <FormHelperText style={styles.error}>
                                {formik?.touched?.videoCourseType && formik?.errors?.videoCourseType}
                            </FormHelperText>
                        </Box>
                    </Box>
                </Box>
            </form>
        </DialogContent>
    )
}

export default AddCourseContent

const ImageDiv = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`
